import { Circle, Info } from "@phosphor-icons/react"
import { Checkbox, Modal, Note, Toast, Tooltip } from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { pick } from "lodash-es"
import { useEffect, useMemo, useState } from "react"
import { createSearchParams, useSearchParams } from "react-router-dom"
import { CustomToast } from "~/components/CustomToast"
import Codenames from "~/constants/Codenames"
import { siteConfig } from "~/constants/config"
import { AppRoutes } from "~/constants/routes"
import { TradeKinds } from "~/constants/Kinds"
import { PayableReceivableDate } from "~/constants/Payable"
import {
  Order_By,
  useInvoiceSettlementSelectorQuery,
  useMapTransactionsForInvoicesMutation,
  useMapTransactionsForReceiptsMutation,
  useMapTransactionsForTradesMutation,
  useNetPayableReceiptSelectorQuery,
  useNetPayableTradeSelectorQuery,
} from "~/generated/graphql"
import { usePayableSelector } from "~/pages/d/payable/advance/PayableSelector"
import { calculateFinalAmount } from "~/pages/pavti/lib/calculate-bifurcation-total"

export const SettleMultiple = ({
  isInvoice = false,
  isOpen,
  onClose,
  plant,
}: {
  isInvoice?: boolean
  isOpen: boolean
  onClose: () => void
  plant: any
}) => {
  const [params, setParams] = useSearchParams()
  const department = plant?.department?.reference_id
  const userId = plant?.user?.id
  const transactionId = plant?.transactions?.[0]?.id
  const page = Number(params.get("subPage") || 1)
  const [state, setState] = useState<Set<string>>(new Set())
  const [initialSelected, setInitialSelected] = useState(false)

  const selected = usePayableSelector(s => s.selected)
  const isTrade = selected !== "receipt"

  const [, mapInvoiceTransactions] = useMapTransactionsForInvoicesMutation()
  const [, mapTradeTransactions] = useMapTransactionsForTradesMutation()
  const [, mapReceiptTransactions] = useMapTransactionsForReceiptsMutation()

  const [{ data: tradesData, fetching: tradesFetching }] =
    useNetPayableTradeSelectorQuery({
      variables: {
        limit: siteConfig.pageSize,
        offset: (page - 1) * siteConfig.pageSize,
        where: {
          current_status_id: {
            _neq: "Draft",
          },
          seller_settlement_status: {
            _in: ["partial", "pending"],
          },
          department: {
            reference_id: {
              _eq: department,
            },
          },
          created_at: {
            _gt: dayjs(PayableReceivableDate).startOf("d"),
          },
          kind: {
            _in: [TradeKinds.InwardPatti, TradeKinds.HishobPatti],
          },
          receipt_id: {
            _is_null: true,
          },
          _or: [
            {
              parent_trade_id: {
                _is_null: true,
              },
              _or: [
                {
                  parent_trade_id: {
                    _is_null: true,
                  },
                },
                {
                  parent_trade: {
                    kind: {
                      _nin: ["inward"],
                    },
                  },
                },
              ],
            },
            {
              parent_trade: {
                kind: {
                  _nin: ["inward"],
                },
              },
            },
          ],
          seller: {
            id: {
              _eq: userId,
            },
          },
        },
        orderBy: {
          created_at: Order_By.AscNullsLast,
        },
      },
      pause: !isOpen || Boolean(isInvoice) || !isTrade,
      requestPolicy: "network-only",
    })

  const [{ data: receiptsData, fetching: receiptsFetching }] =
    useNetPayableReceiptSelectorQuery({
      variables: {
        limit: siteConfig.pageSize,
        offset: (page - 1) * siteConfig.pageSize,
        where: {
          seller_settlement_status: {
            _in: ["partial", "pending"],
          },
          kind: {
            _eq: "final",
          },
          department: {
            reference_id: {
              _eq: department,
            },
          },
          created_at: {
            _gt: dayjs(PayableReceivableDate).startOf("d"),
          },
          seller: {
            id: {
              _eq: userId,
            },
          },
        },
        orderBy: {
          created_at: Order_By.AscNullsLast,
        },
      },
      pause: !isOpen || Boolean(isInvoice) || isTrade,
      requestPolicy: "network-only",
    })

  const [{ data: invoiceData, fetching: invoiceFetching }] =
    useInvoiceSettlementSelectorQuery({
      variables: {
        limit: siteConfig.pageSize,
        offset: (page - 1) * siteConfig.pageSize,
        where: {
          buyer_settlement_status: {
            _in: ["partial", "pending"],
          },
          current_status_id: {
            _neq: Codenames.InvoiceCancelled,
          },
          created_at: {
            _gt: dayjs(PayableReceivableDate).startOf("d"),
          },
          department: {
            reference_id: {
              _eq: department,
            },
          },
          buyer: {
            id: {
              _eq: userId,
            },
          },
        },
        orderBy: {
          created_at: Order_By.AscNullsLast,
        },
      },
      pause: !isOpen || !isInvoice,
      requestPolicy: "network-only",
    })

  const totalTrades =
    (isInvoice
      ? invoiceData?.invoice_aggregate?.aggregate?.count
      : isTrade
      ? tradesData?.trade_aggregate?.aggregate?.count
      : receiptsData?.receipt_aggregate?.aggregate?.count) || 0

  const selectableTradeIds = useMemo(
    () =>
      (tradesData?.trade || [])
        .filter(trade => !trade?.has_advance_release_pending)
        .map(trade => String(trade.id)),
    [tradesData]
  )

  useEffect(() => {
    if (isOpen) {
      if (isInvoice && invoiceData?.invoice && !initialSelected) {
        setState(prev => {
          const newState = new Set(prev)
          invoiceData.invoice.forEach(invoice =>
            newState.add(String(invoice.id))
          )
          return newState
        })
        setInitialSelected(true)
      } else if (isTrade && tradesData?.trade && !initialSelected) {
        setState(new Set(selectableTradeIds))
        setInitialSelected(true)
      } else if (!isTrade && receiptsData?.receipt && !initialSelected) {
        setState(prev => {
          const newState = new Set(prev)
          receiptsData.receipt.forEach(receipt =>
            newState.add(String(receipt.id))
          )
          return newState
        })
        setInitialSelected(true)
      }
    } else {
      setInitialSelected(false)
      setState(new Set())
    }
  }, [
    tradesData,
    receiptsData,
    invoiceData,
    isOpen,
    initialSelected,
    isInvoice,
    isTrade,
  ])

  const onSubmit = async () => {
    if (isInvoice) {
      const { error, data: inv } = await mapInvoiceTransactions({
        input: Array.from(state).map((e: string) => {
          return {
            invoice_id: e,
            transactions: [transactionId],
          }
        }),
      })
      onClose?.()
      if (error) {
        return CustomToast({
          intent: "danger",
          title: "Something went wrong! " + error.message,
        })
      }
      return CustomToast({
        title: "Successfully added Plant Transaction!",
        subtitle: `Invoice Ids: ${inv?.map_invoice_transaction_many?.invoices
          ?.map(t => t?.id)
          .join(", ")}`,
      })
    }
    if (isTrade) {
      const { error, data: tradeIds } = await mapTradeTransactions({
        input: Array.from(state)
          .filter(id => {
            const trade = tradesData?.trade?.find(t => String(t.id) === id)
            return !trade?.has_advance_release_pending
          })
          .map((e: string) => {
            return {
              trade_id: e,
              transactions: [transactionId],
            }
          }),
      })
      onClose?.()
      if (error) {
        return CustomToast({
          intent: "danger",
          title: "Something went wrong! " + error.message,
        })
      }
      return CustomToast({
        title: "Successfully added Plant Transaction!",
        subtitle: `Trade Ids: ${tradeIds?.map_trade_transaction_many?.trades
          ?.map(t => t?.id)
          .join(", ")}`,
      })
    }
    const { error, data: receiptIds } = await mapReceiptTransactions({
      input: Array.from(state).map((e: string) => {
        return {
          receipt_id: e,
          transactions: [transactionId],
        }
      }),
    })
    onClose?.()
    if (error) {
      return CustomToast({
        intent: "danger",
        title: "Something went wrong! " + error.message,
      })
    }
    return CustomToast({
      title: "Successfully added Plant Transaction!",
      subtitle: `Receipt Ids: ${receiptIds?.map_receipt_transaction_many?.receipts
        ?.map(r => r?.id)
        .join(", ")}`,
    })
  }

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      title={`Outstanding ${
        isInvoice ? "Invoice" : isTrade ? "Trade" : "Receipt"
      } Details or Settlement Action`}
      bodyClassName="overflow-visible max-w-5xl"
      primaryActionButtonProps={{
        text: "Yes, Confirm",
        onClick: onSubmit,
      }}
      secondaryActionButtonProps={{
        text: "Cancel",
        onClick: onClose,
      }}
    >
      <div className="">
        <TableView
          data={
            isInvoice
              ? invoiceData?.invoice || []
              : isTrade
              ? tradesData?.trade || []
              : receiptsData?.receipt || []
          }
          isLoading={
            isInvoice
              ? invoiceFetching
              : isTrade
              ? tradesFetching
              : receiptsFetching
          }
          paginationProps={{
            total: Math.ceil(totalTrades / siteConfig.pageSize),
            page: page,
            onChange(page) {
              params.set("subPage", page.toString())
              params.set("search", "")
              setParams(params)
            },
          }}
          tableClassName="max-h-[50vh] min-h-[50vh] overflow-y-auto"
          columns={[
            {
              id: "select",
              Header: (
                <Checkbox
                  checked={
                    isInvoice
                      ? state.size === invoiceData?.invoice?.length
                      : isTrade
                      ? state.size === selectableTradeIds.length
                      : state.size === receiptsData?.receipt?.length
                  }
                  onChange={e => {
                    if (e) {
                      setState(prev => {
                        const newState = new Set(prev)
                        if (isInvoice) {
                          invoiceData?.invoice?.forEach(invoice =>
                            newState.add(String(invoice.id))
                          )
                        } else if (isTrade) {
                          selectableTradeIds.forEach(id => newState.add(id))
                        } else {
                          receiptsData?.receipt?.forEach(receipt =>
                            newState.add(String(receipt.id))
                          )
                        }
                        return newState
                      })
                    } else {
                      setState(new Set())
                    }
                  }}
                />
              ),
              accessor: "id",
              Cell: ({ row }) => {
                const id = row.original.id.toString()
                const blocked = row.original.has_advance_release_pending
                return (
                  <Tooltip
                    disabled={!blocked}
                    content={
                      blocked
                        ? "Advance Release Pending. Please approve or reject the advance release before settling this trade."
                        : ""
                    }
                  >
                    <span>
                      <Checkbox
                        checked={state.has(id)}
                        disabled={blocked}
                        onChange={e => {
                          if (e) {
                            setState(prev => new Set([...prev, id]))
                          } else {
                            setState(prev => {
                              const newState = new Set(prev)
                              newState.delete(id)
                              return newState
                            })
                          }
                        }}
                      />
                    </span>
                  </Tooltip>
                )
              },
            },
            {
              Header: "Created At",
              accessor: "created_at",
              Cell: ({ value }) => {
                return (
                  <Cell
                    title={dayjs(value).format("DD MMM 'YY")}
                    subtitle={dayjs(value).format("hh:mm A")}
                  />
                )
              },
            },
            {
              Header: isInvoice
                ? "Invoice Id"
                : isTrade
                ? "Trade Id"
                : "Receipt Id",
              accessor: "id",
              Cell: ({ value }) => {
                return <Cell title={`#${value}`} />
              },
            },
            {
              Header: "Total Amount",
              accessor: !isInvoice ? "seller_bifurcation" : "buyer_bifurcation",
              Cell: ({ value }) => {
                const gst = ["buyer_tax_amount", "seller_tax_amount"]
                const expense = [
                  "buyer_expense_amount",
                  "seller_expense_amount",
                ]

                const deductedAmount = "deducted_amount"
                const expenseTax = [
                  "buyer_expense_tax_amount",
                  "seller_expense_tax_amount",
                ]

                const supplierCharge = "supplier_charge"

                const total = calculateFinalAmount({
                  ...pick(
                    value,
                    deductedAmount,
                    ...gst,
                    ...expense,
                    ...expenseTax,
                    supplierCharge
                  ),
                })

                return (
                  <Cell
                    title={inr(total)}
                    titleClassName="!text-green font-600"
                  />
                )
              },
            },
            {
              Header: "Paid Amount",
              accessor: isInvoice
                ? "buyer_bifurcation.received_amount.val"
                : "seller_bifurcation.paid_amount.val",
              Cell: ({ value, row: { original } }) => {
                const advanceUtilized =
                  original?.seller_bifurcation?.advance_amount?.val || undefined
                return (
                  <Cell
                    title={inr(value)}
                    titleClassName="!text-green font-600"
                    subtitle={
                      isTrade ? (
                        advanceUtilized ? (
                          <div className="flex gap-1 items-center">
                            <Info /> Avd Utilized <br />
                            {inr(advanceUtilized)}
                          </div>
                        ) : (
                          ""
                        )
                      ) : (
                        ""
                      )
                    }
                  />
                )
              },
            },
            {
              Header: isInvoice ? "Receivable Amount" : "Payable Amount",
              accessor: isInvoice
                ? "buyer_bifurcation.total"
                : "seller_bifurcation.total",
              Cell: ({ value }) => {
                return (
                  <Cell
                    title={inr(value)}
                    titleClassName="!text-red font-600"
                  />
                )
              },
            },
            {
              Header: "Status",
              accessor: isInvoice
                ? "buyer_settlement_status"
                : "seller_settlement_status",
              Cell: ({ value, row }) => {
                const blocked = row.original.has_advance_release_pending
                return (
                  <Cell
                    titleClassName={classNames(
                      value === "pending" || blocked
                        ? "text-yellow"
                        : "text-blue"
                    )}
                    title={
                      <div className="flex items-center gap-1">
                        <Circle weight="fill" />
                        {blocked ? (
                          <div className="flex items-center gap-1">
                            Advance Release Pending
                          </div>
                        ) : value === "pending" ? (
                          "Pending"
                        ) : (
                          "Partial"
                        )}
                      </div>
                    }
                  />
                )
              },
            },
          ]}
        />
      </div>
      {isTrade &&
        tradesData?.trade?.some(t => t.has_advance_release_pending) && (
          <Toast
            className="min-w-full text-xs mb-3 !shadow-none !border-1 !border-yellow-200 !rounded-lg"
            title={
              <div className="flex justify-between !min-w-full">
                <div>Settlement Restriction Note</div>
                <div
                  className="text-[10px] underline cursor-pointer"
                  onClick={() =>
                    window.open(
                      `${AppRoutes.advancePayable}`,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >{`View Advance Request >>`}</div>
              </div>
            }
            subtitle={
              "Trades with an Advance Release Pending cannot be selected for settlement. Please approve or reject the advance release request before settling the trade."
            }
            intent="warning"
            variant="minimal"
          />
        )}
      <Note
        intent="info"
        className="rounded-lg text-gray-800 font-600"
        title="Do you want to proceed with the selected trades for settlement?"
      />
    </Modal>
  )
}
